import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { PieceDetail } from '../api'
import { describeError } from '../../lib/describeError'
import { safeHref } from '../../lib/html'
import { choosePhoto, getPhotoOptions, pushPhotoCredit, removePhoto, uploadPhoto } from './photoApi'
import { canRemovePhoto, photoLabel, photoOk, photoOptionsNote, removePhotoQuestion, usedOnLabel } from './photoFit'
import { objectPosition } from './framing'
import { LETTERBOX_BG } from './FramingDialog'
import { missingCredit, SAVE_FIRST } from './pieceHelpers'
import { btn, btnPrimary, btnSmall, errorText, field, focus } from './ui'

/** The lead photograph at the head of the page: the picture, its credit line and Change photo. */
/** What a photo write needs from the page: save the edits first, then adopt the returned copy. */
type Writes = {
  /** False refuses the write: edits are waiting that could not be saved. */
  beforeWrite: () => Promise<boolean>
  onDetail: (d: PieceDetail) => void
}

/** Remove photo's click: ask first, and send nothing unless the answer is yes. */
export function confirmThenRemove(published: boolean, confirm: (question: string) => boolean, remove: () => void): void {
  if (confirm(removePhotoQuestion(published))) remove()
}

async function guarded<T>(w: Writes, fn: () => Promise<T>): Promise<T> {
  if (!(await w.beforeWrite())) throw new Error(SAVE_FIRST)
  return fn()
}

export function PhotoBlock({ detail, credit, onCredit, canEditCredit, canChange, canPushCredit, onChanged, onFraming, beforeWrite, onDetail }: Writes & {
  detail: PieceDetail
  /** The unsaved credit, or undefined when the saved one stands. */
  credit: string | undefined
  /** Autosaves featured_caption_override; only while the piece is open for edits. */
  onCredit: (v: string) => void
  canEditCredit: boolean
  canChange: boolean
  /** Send credit to the site (a live piece): editors only; a writer's credit stays read only. */
  canPushCredit: boolean
  onChanged: () => void
  /** Open the framing dialog for this photo. */
  onFraming: () => void
}) {
  const [drawer, setDrawer] = useState(false)
  const [liveCredit, setLiveCredit] = useState<string | null>(null)
  const img = safeHref(detail.featured_image_url)
  // a live piece's credit is editable only by someone who may send it to the site
  const published = detail.status === 'published' && canPushCredit
  const writes: Writes = { beforeWrite, onDetail }
  const push = useMutation({
    mutationFn: () => guarded(writes, () => pushPhotoCredit(detail.id, liveCredit)),
    onSuccess: (r) => { setLiveCredit(null); onDetail(r as PieceDetail); onChanged() },
  })
  const remove = useMutation({
    mutationFn: () => guarded(writes, () => removePhoto(detail.id)),
    onSuccess: (r) => { setLiveCredit(null); onDetail(r as PieceDetail); onChanged() },
  })
  const onRemove = () => confirmThenRemove(detail.status === 'published', (q) => window.confirm(q), () => remove.mutate())
  // featured_framing is in fractions of the photo shown, as object-position wants.
  const ff = detail.featured_framing
  const focal: [number, number] | null = ff?.focal_x != null && ff.focal_y != null ? [ff.focal_x, ff.focal_y] : null
  const letterbox = ff?.fit?.card === 'contain'
  const shown = published ? (liveCredit ?? detail.featured_caption ?? '') : (credit ?? detail.featured_caption ?? '')

  return (
    <figure className="m-0 mb-7 flex flex-col gap-2">
      {img
        ? <img src={img} alt="" className={`aspect-video w-full rounded ${letterbox ? 'object-contain' : 'object-cover'}`}
               style={letterbox ? { background: LETTERBOX_BG } : { objectPosition: objectPosition(focal) }} />
        : (
          <div className="flex aspect-video w-full items-center justify-center rounded bg-ink-600 font-ui text-[13px] font-semibold text-paper-dim">
            No photograph yet
          </div>
        )}
      <figcaption className="flex flex-wrap items-center justify-between gap-2 font-ui text-[13px] text-paper-dim">
        {img && (canEditCredit || published) ? (
          <label className="flex min-w-0 flex-[1_1_260px] flex-col gap-1">
            <span className="sr-only">Photo credit</span>
            <input
              type="text" value={shown} placeholder="Caption and credit, e.g. (Photo: U.S. Navy)"
              onChange={(e) => (published ? setLiveCredit(e.target.value) : onCredit(e.target.value))}
              className={`w-full rounded border border-transparent bg-transparent px-1 py-1 font-ui text-[13px] text-paper-dim hover:border-ink-500 ${focus}`}
            />
          </label>
        ) : (
          <span className="min-w-0 flex-[1_1_260px]">{img ? shown : 'Choose one from the sources or upload your own.'}</span>
        )}
        <span className="flex flex-wrap gap-2">
          {canRemovePhoto(img, canChange) && (
            <button type="button" className={btnSmall} disabled={remove.isPending} onClick={onRemove}>
              {remove.isPending ? 'Removing…' : 'Remove photo'}
            </button>
          )}
          {canChange && img && <button type="button" className={btnSmall} onClick={onFraming}>Framing</button>}
          {canChange && <button type="button" className={btnSmall} onClick={() => setDrawer(true)}>Change photo</button>}
        </span>
      </figcaption>
      {missingCredit(img, detail.featured_caption_source) && (
        <p role="status" className="m-0 rounded-lg bg-amber-glow/20 px-3 py-2 font-ui text-[13px] font-semibold text-amber-deep">
          No photo credit yet. Check the source article before publishing — do not guess.
        </p>
      )}
      {published && liveCredit !== null && (
        <div className="flex flex-wrap items-center gap-2 font-ui text-[12px] text-paper-faint">
          <button type="button" className={btnSmall} disabled={push.isPending} onClick={() => push.mutate()}>
            {push.isPending ? 'Sending…' : 'Send credit to the site'}
          </button>
          Updates the caption on the live piece.
        </div>
      )}
      {push.isError && <p className={errorText}>{describeError(push.error)}</p>}
      {remove.isError && <p className={errorText}>{describeError(remove.error)}</p>}
      {detail.featured_photo_article && (
        <p className="m-0 font-ui text-[12px] text-paper-faint">
          Photo from{' '}
          <Link className="underline" to={`/lab/archive/${detail.featured_photo_article.article_id}`}>
            {detail.featured_photo_article.title ?? 'the source article'}
          </Link>
        </p>
      )}
      {drawer && <PhotoDrawer detail={detail} writes={writes} onClose={() => setDrawer(false)} onChanged={onChanged} />}
    </figure>
  )
}

function PhotoDrawer({ detail, writes, onClose, onChanged }: { detail: PieceDetail; writes: Writes; onClose: () => void; onChanged: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [credit, setCredit] = useState('')
  const options = useQuery({ queryKey: ['section-draft-photo-options', detail.id], queryFn: () => getPhotoOptions(detail.id) })
  const choose = useMutation({
    mutationFn: (assetId: number) => guarded(writes, () => choosePhoto(detail.id, assetId)),
    onSuccess: (r) => { writes.onDetail(r as PieceDetail); onChanged() },
  })
  const upload = useMutation({
    mutationFn: () => guarded(writes, () => uploadPhoto(detail.id, file as File, credit)),
    onSuccess: (r) => { setFile(null); setCredit(''); writes.onDetail(r as PieceDetail); onChanged(); onClose() },
  })
  const items = options.data?.items ?? []
  const note = options.data ? photoOptionsNote(options.data) : null
  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-ink-900/60 font-ui" role="presentation" onClick={onClose}>
      <aside
        role="dialog" aria-modal="true" aria-labelledby="photo-drawer-title"
        className="flex h-full w-full max-w-[520px] flex-col gap-4 overflow-y-auto border-l border-ink-500 bg-ink-700 p-5 text-paper"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 id="photo-drawer-title" className="m-0 text-[17px] font-semibold">Change photo</h2>
          <button type="button" className={btnSmall} onClick={onClose} autoFocus>Close</button>
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="m-0 text-[14px] font-semibold">From the archive</h3>
          {options.isPending && <p role="status" className="m-0 text-[13px] text-paper-dim">Loading the photographs…</p>}
          {options.isError && <p className={errorText}>{describeError(options.error)}</p>}
          {options.isSuccess && items.length === 0 && <p className="m-0 text-[13px] text-paper-dim">The archive has no photographs to offer. Upload one below.</p>}
          {note && <p className="m-0 text-[12px] text-paper-faint">{note}</p>}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {items.map((o) => {
              const inUse = o.image_url === detail.featured_image_url
              return (
                <button key={o.asset_id} type="button" title={o.article?.title ?? o.maker?.page_title ?? ''}
                        onClick={() => choose.mutate(o.asset_id)} disabled={choose.isPending || inUse}
                        className={`flex flex-col gap-1 rounded-lg border p-1.5 text-left disabled:cursor-default ${focus} ${
                          inUse ? 'border-amber-glow' : photoOk(o) ? 'border-ink-400 hover:bg-ink-600' : 'border-ink-500 opacity-75 hover:bg-ink-600'}`}>
                  <img src={safeHref(o.image_url)} alt="" loading="lazy" className="h-20 w-full rounded object-cover" />
                  <span className="truncate text-[12px] text-paper-dim">{o.credit ?? 'No credit'}</span>
                  <span className="text-[11px] font-semibold text-paper-faint">{inUse ? 'In use' : photoLabel(o)}</span>
                  {o.fit_reason && <span className="text-[11px] text-paper-dim">{o.fit_reason}</span>}
                  {usedOnLabel(o) && <span className="text-[11px] font-semibold text-amber-deep">{usedOnLabel(o)}</span>}
                </button>
              )
            })}
          </div>
          {choose.isError && <p className={errorText}>{describeError(choose.error)}</p>}
        </section>

        <form className="flex flex-col gap-2 border-t border-ink-500 pt-4"
              onSubmit={(e) => { e.preventDefault(); if (file && !upload.isPending) upload.mutate() }}>
          <h3 className="m-0 text-[14px] font-semibold">Upload your own</h3>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
            Photograph
            <input type="file" accept="image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                   className="text-[13px] font-normal text-paper" />
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
            Credit (optional)
            <input type="text" value={credit} placeholder="e.g. (Photo: Alex Mladenov)" onChange={(e) => setCredit(e.target.value)} className={field} />
          </label>
          <p className="m-0 text-[12px] text-paper-faint">
            JPEG or PNG. The credit can be added later, under the photo.{detail.status === 'published' ? ' The live piece is updated too.' : ''}
          </p>
          <div>
            <button type="submit" className={file ? btnPrimary : btn} disabled={!file || upload.isPending}>
              {upload.isPending ? 'Uploading…' : 'Upload photograph'}
            </button>
          </div>
          {upload.isError && <p className={errorText}>{describeError(upload.error)}</p>}
        </form>
      </aside>
    </div>
  )
}

